import { MapPin, Wrench, Calendar, Gauge, AlertCircle, Clock } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const VehicleCard = ({ vehicle, onClick, viewMode = 'grid' }) => {
  const { getThemeClasses, theme } = useTheme();
  const themeClasses = getThemeClasses();
  // Get health color based on percentage with better contrast
  const getHealthColor = (health) => {
    if (health >= 80) return 'text-green-400 border-green-400';
    if (health >= 60) return 'text-amber-400 border-amber-400';
    if (health >= 40) return 'text-orange-400 border-orange-400';
    return 'text-red-400 border-red-400';
  };

  // Get health background color
  const getHealthBgColor = (health) => {
    if (health >= 80) return 'bg-green-500/15';
    if (health >= 60) return 'bg-amber-500/15';
    if (health >= 40) return 'bg-orange-500/15';
    return 'bg-red-500/15';
  };

  // Get alert color with theme support
  const getAlertColor = (level) => {
    if (theme === 'light') {
      switch (level) {
        case 'high':
          return 'bg-red-100 border-red-300 text-red-800';
        case 'medium':
          return 'bg-amber-100 border-amber-300 text-amber-800';
        case 'low':
          return 'bg-green-100 border-green-300 text-green-800';
        default:
          return `${themeClasses.secondary} ${themeClasses.border} ${themeClasses.textMuted}`;
      }
    } else {
      switch (level) {
        case 'high':
          return 'bg-red-600/20 border-red-500/40 text-red-400';
        case 'medium':
          return 'bg-amber-600/20 border-amber-500/40 text-amber-400';
        case 'low':
          return 'bg-green-600/20 border-green-500/40 text-green-400';
        default:
          return `${themeClasses.secondary} ${themeClasses.border} ${themeClasses.textMuted}`;
      }
    }
  };

  // Get alert badge color
  const getAlertBadgeColor = (level) => {
    switch (level) {
      case 'high':
        return 'bg-red-500 text-white';
      case 'medium':
        return 'bg-amber-500 text-white';
      case 'low':
        return 'bg-green-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  // Get all components with issues (health < 80%)
  const getComponentIssues = () => {
    const components = vehicle.components || { engine: 0, suspension: 0, brakes: 0, transmission: 0 };
    const issues = [];
    
    Object.entries(components).forEach(([name, healthValue]) => {
      // Extract numeric health value (handle both old format (number) and new format (object))
      let health = 0;
      if (typeof healthValue === 'object' && healthValue !== null && 'health' in healthValue) {
        health = healthValue.health || 0;
      } else if (typeof healthValue === 'number') {
        health = healthValue;
      }
      
      // Skip non-component fields like 'id', 'vehicleId', etc.
      if (['engine', 'suspension', 'brakes', 'transmission'].includes(name) && health < 80) {
        issues.push({
          name: name.charAt(0).toUpperCase() + name.slice(1),
          health: health
        });
      }
    });
    
    // Sort by health (lowest first)
    return issues.sort((a, b) => a.health - b.health);
  };

  const componentIssues = getComponentIssues();

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'No Service';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ============================================================================
  // PHASE 7: Service Due and Cumulative Wear Display
  // ============================================================================
  
  // Get alarm color for service due and lifespan
  const getAlarmColor = (alarmLevel) => {
    switch (alarmLevel) {
      case 3: return 'text-red-500 border-red-500 bg-red-500/10'; // Critical
      case 2: return 'text-orange-500 border-orange-500 bg-orange-500/10'; // Warning/Required
      case 1: return 'text-amber-500 border-amber-500 bg-amber-500/10'; // Caution/Recommended
      default: return 'text-green-500 border-green-500 bg-green-500/10'; // Normal
    }
  };
  
  // Get worst service due alarm
  const getWorstServiceDueAlarm = () => {
    if (!vehicle.serviceDueAlarms) return { level: 0, status: 'normal', components: [] };
    
    const alarms = Object.entries(vehicle.serviceDueAlarms).map(([name, alarm]) => ({
      name,
      ...alarm
    }));
    
    const worstLevel = Math.max(...alarms.map(a => a.alarmLevel));
    const worstAlarms = alarms.filter(a => a.alarmLevel === worstLevel);
    
    return {
      level: worstLevel,
      status: worstAlarms[0]?.alarmStatus || 'normal',
      components: worstAlarms
    };
  };
  
  // Get worst lifespan alarm
  const getWorstLifespanAlarm = () => {
    if (!vehicle.lifespanAlarms) return { level: 0, status: 'normal', components: [] };
    
    const alarms = Object.entries(vehicle.lifespanAlarms).map(([name, alarm]) => ({
      name,
      ...alarm
    }));
    
    const worstLevel = Math.max(...alarms.map(a => a.alarmLevel));
    const worstAlarms = alarms.filter(a => a.alarmLevel === worstLevel);
    
    return {
      level: worstLevel,
      status: worstAlarms[0]?.alarmStatus || 'normal',
      components: worstAlarms
    };
  };
  
  const serviceDueAlarm = getWorstServiceDueAlarm();
  const lifespanAlarm = getWorstLifespanAlarm();



  // List view layout
  if (viewMode === 'list') {
    return (
      <div
        onClick={onClick}
        className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-4 hover:border-military-500/50 transition-all duration-200 cursor-pointer shadow-md`}
      >
        <div className="flex items-center gap-6">
          {/* Left: Model, Brand, and Year */}
          <div className="flex-shrink-0 w-48">
            <h3 className={`text-lg font-bold ${themeClasses.textPrimary} mb-1`}>{vehicle.model}</h3>
            <p className={`${themeClasses.textMuted} text-sm`}>{vehicle.brand}</p>
            <p className={`${themeClasses.textMuted} text-xs mt-1`}>Year: {vehicle.year || '2020'}</p>
          </div>

          {/* Middle: Health Circle, Alert and Location */}
          <div className="flex-1 flex items-center gap-6">
            {/* Health Circle */}
            <div className={`relative flex items-center justify-center w-12 h-12 rounded-full border-2 ${getHealthColor(vehicle.health)} ${getHealthBgColor(vehicle.health)}`}>
              <div className="text-center">
                <span className={`text-xs font-bold ${getHealthColor(vehicle.health)}`}>{vehicle.health}%</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded text-xs font-semibold ${getAlertBadgeColor(vehicle.alertLevel)}`}>
                {vehicle.alertLevel.toUpperCase()}
              </span>
            </div>

            <div className={`flex items-center ${themeClasses.textSecondary} text-sm`}>
              <MapPin size={14} className="mr-2 text-blue-400" />
              <span className="text-xs">
                {vehicle.region} - {vehicle.baseName}, {vehicle.baseLocation}
              </span>
            </div>

            {componentIssues.length > 0 && (
              <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border ${getAlertColor(vehicle.alertLevel)}`}>
                <Wrench size={14} />
                <div className="flex flex-wrap gap-1">
                  {componentIssues.map((issue, idx) => (
                    <span key={idx} className={`text-xs font-semibold ${
                      vehicle.alertLevel === 'high' ? (theme === 'light' ? 'text-red-800' : 'text-red-400') : 
                      vehicle.alertLevel === 'medium' ? (theme === 'light' ? 'text-amber-800' : 'text-amber-400') : 
                      themeClasses.textPrimary
                    }`}>
                      {issue.name} ({issue.health}%){idx < componentIssues.length - 1 ? ',' : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Metrics */}
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className={`${themeClasses.textMuted} text-xs`}>Mileage</p>
              <p className={`${themeClasses.textPrimary} text-sm font-semibold`}>{Math.round(vehicle.mileage).toLocaleString()} mi</p>
            </div>
            <div className="text-center">
              <p className={`${themeClasses.textMuted} text-xs`}>Last Service</p>
              <p className={`${themeClasses.textPrimary} text-sm font-semibold`}>{formatDate(vehicle.lastMaintenance)}</p>
            </div>

            {/* PHASE 7: Service Due Alarm */}
            {vehicle.serviceDueAlarms && serviceDueAlarm.level > 0 && (
              <div className={`px-3 py-2 rounded-lg border ${getAlarmColor(serviceDueAlarm.level)}`}>
                <div className="flex items-center gap-2">
                  <Clock size={14} />
                  <div>
                    <p className="text-xs font-medium">Service Due</p>
                    <p className="text-xs font-semibold capitalize">{String(serviceDueAlarm.status).replace('_', ' ')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* PHASE 7: Lifespan Alarm */}
            {vehicle.lifespanAlarms && lifespanAlarm.level > 0 && (
              <div className={`px-3 py-2 rounded-lg border ${getAlarmColor(lifespanAlarm.level)}`}>
                <div className="flex items-center gap-2">
                  <AlertCircle size={14} />
                  <div>
                    <p className="text-xs font-medium">Lifespan</p>
                    <p className="text-xs font-semibold capitalize">
                      {String(lifespanAlarm.status)}
                      {lifespanAlarm.components[0]?.triggeredBy && ` (${String(lifespanAlarm.components[0].triggeredBy)})`}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Grid view layout (original)
  return (
    <div
      onClick={onClick}
      className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-4 hover:border-military-500/50 hover:scale-105 transform transition-all duration-200 cursor-pointer shadow-md`}
    >
      {/* Header with Alert Badge and Health Circle */}
      <div className={`flex items-start justify-between mb-3 pb-3 border-b ${themeClasses.border}`}>
        <div className="flex-1">
          <h3 className={`text-lg font-bold ${themeClasses.textPrimary} mb-1`}>{vehicle.model}</h3>
          <p className={`${themeClasses.textMuted} text-sm`}>{vehicle.brand}</p>
          <p className={`${themeClasses.textMuted} text-xs mt-1`}>Year: {vehicle.year || '2020'}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Health Circle */}
          <div className={`relative flex items-center justify-center w-14 h-14 rounded-full border-2 ${getHealthColor(vehicle.health)} ${getHealthBgColor(vehicle.health)}`}>
            <div className="text-center">
              <span className={`text-sm font-bold ${getHealthColor(vehicle.health)}`}>{vehicle.health}%</span>
            </div>
          </div>
          <span className={`px-2 py-1 rounded text-xs font-semibold ${getAlertBadgeColor(vehicle.alertLevel)}`}>
            {vehicle.alertLevel.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Component Issues Alert */}
      {componentIssues.length > 0 && (
        <div className={`mb-3 p-2 rounded-lg border ${getAlertColor(vehicle.alertLevel)}`}>
          <div className="flex items-start gap-2">
            <Wrench size={16} className="mt-0.5" />
            <div className="flex-1">
              <p className={`text-xs font-semibold mb-1 ${
                vehicle.alertLevel === 'high' ? (theme === 'light' ? 'text-red-800' : 'text-red-400') : 
                vehicle.alertLevel === 'medium' ? (theme === 'light' ? 'text-amber-800' : 'text-amber-400') : 
                themeClasses.textPrimary
              }`}>
                Component Issues
              </p>
              <div className="space-y-1">
                {componentIssues.map((issue, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className={`text-xs ${
                      vehicle.alertLevel === 'high' ? (theme === 'light' ? 'text-red-700' : 'text-red-300') : 
                      vehicle.alertLevel === 'medium' ? (theme === 'light' ? 'text-amber-700' : 'text-amber-300') : 
                      themeClasses.textSecondary
                    }`}>
                      {issue.name}
                    </span>
                    <span className={`text-xs font-semibold ${
                      vehicle.alertLevel === 'high' ? (theme === 'light' ? 'text-red-800' : 'text-red-400') : 
                      vehicle.alertLevel === 'medium' ? (theme === 'light' ? 'text-amber-800' : 'text-amber-400') : 
                      themeClasses.textPrimary
                    }`}>
                      {issue.health}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Location Info */}
      <div className="space-y-2 mb-3">
        <div className={`flex items-center ${themeClasses.textSecondary} text-sm`}>
          <MapPin size={14} className="mr-2 text-blue-400" />
          <span className="text-xs">
            {vehicle.region} - {vehicle.baseName}, {vehicle.baseLocation}
          </span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {/* Mileage */}
        <div className={`${themeClasses.card} rounded-lg p-2 border ${themeClasses.border}`}>
          <div className="flex items-center gap-1 mb-1">
            <Gauge size={12} className={themeClasses.textMuted} />
            <p className={`${themeClasses.textMuted} text-xs`}>Mileage</p>
          </div>
          <p className={`${themeClasses.textPrimary} text-sm font-semibold`}>{Math.round(vehicle.mileage).toLocaleString()} mi</p>
        </div>

        {/* Last Maintenance */}
        <div className={`${themeClasses.card} rounded-lg p-2 border ${themeClasses.border}`}>
          <div className="flex items-center gap-1 mb-1">
            <Calendar size={12} className={themeClasses.textMuted} />
            <p className={`${themeClasses.textMuted} text-xs`}>Last Service</p>
          </div>
          <p className={`${themeClasses.textPrimary} text-sm font-semibold`}>{formatDate(vehicle.lastMaintenance)}</p>
        </div>
      </div>

      {/* PHASE 7: Service Due and Cumulative Wear Alarms */}
      {(vehicle.serviceDueAlarms || vehicle.lifespanAlarms) && (
        <div className="grid grid-cols-2 gap-2">
          {/* Service Due Alarm */}
          {vehicle.serviceDueAlarms && (
            <div className={`rounded-lg p-2 border ${getAlarmColor(serviceDueAlarm.level)}`}>
              <div className="flex items-center gap-1 mb-1">
                <Clock size={12} />
                <p className="text-xs font-medium">Service Due</p>
              </div>
              <p className="text-xs font-semibold capitalize">{String(serviceDueAlarm.status).replace('_', ' ')}</p>
              {serviceDueAlarm.components.length > 0 && (
                <p className="text-xs mt-1 opacity-80">
                  {serviceDueAlarm.components.map(c => String(c.name).charAt(0).toUpperCase() + String(c.name).slice(1)).join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Cumulative Wear Alarm */}
          {vehicle.lifespanAlarms && (
            <div className={`rounded-lg p-2 border ${getAlarmColor(lifespanAlarm.level)}`}>
              <div className="flex items-center gap-1 mb-1">
                <AlertCircle size={12} />
                <p className="text-xs font-medium">Lifespan</p>
              </div>
              <p className="text-xs font-semibold capitalize">{String(lifespanAlarm.status)}</p>
              {lifespanAlarm.components.length > 0 && lifespanAlarm.components[0]?.triggeredBy && (
                <p className="text-xs mt-1 opacity-80">
                  ⚠️ {String(lifespanAlarm.components[0].triggeredBy)}
                </p>
              )}
            </div>
          )}
        </div>
      )}


    </div>
  );
};

export default VehicleCard;
